import { Box } from '@factorialco/deprecated-design-system'

import type { PanesHeaderInlineInfoProps } from 'modules/core/components/PanesHeader'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { dateToString, stringToDate } from 'modules/core/lib/dates'
import hasFeature from 'modules/core/lib/hasFeature'
import i18n from 'modules/core/lib/i18n'

import Page from 'modules/layout/components/Page'
import Header from 'modules/layout/components/Page/Header'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'
import Answer from 'modules/reviews/components/Answer'
import { useGroups } from 'modules/reviews/hooks/useGroups'
import { useReview } from 'modules/reviews/hooks/useReview'
import { ScoreProvider } from 'modules/reviews/lib/PerformanceScoreContext'
import { getGroupName } from 'modules/reviews/lib/getGroupName'
import type QuestionGroup from 'modules/reviews/store/models/QuestionGroup'
import type Review from 'modules/reviews/store/models/v2/Review'

import EditableUntilBanner from './EditableUntilBanner'

interface Props {
  formId: number
  loadingQuestionGroup: boolean
  loadingCompetencies: boolean
  questionGroup: QuestionGroup | null | undefined
  refresh: () => Promise<void>
  back: string
  useResponsivePage?: boolean
}

export default function ReviewAnswer({
  formId,
  loadingQuestionGroup,
  loadingCompetencies,
  questionGroup,
  refresh,
  back,
  useResponsivePage = false,
}: Props) {
  useGroups()

  const { loading: loadingReview, review } = useReview(formId.toString())
  const reviewName = review?.attributes.name
  const respondent = questionGroup?.respondent
  const {
    access: { id: userId },
  } = useCurrentSession()
  const isUserTheRespondent = respondent?.id === userId

  const inlineInfo = (): PanesHeaderInlineInfoProps[] => {
    const inlineInfo: PanesHeaderInlineInfoProps[] = []

    if (!questionGroup || !review) return inlineInfo

    const group = questionGroup.respondentGroup

    if (!group) return inlineInfo

    inlineInfo.push({
      icon: 'pencil',
      message: getGroupName(group) || '',
    })

    if (review.isAnonymous) {
      inlineInfo.push({
        icon: 'lock-locked',
        message: i18n.t('surveys.answer.anonymous'),
      })
    } else {
      inlineInfo.push({
        icon: 'eye-visible',
        message: i18n.t('surveys.answer.visible'),
      })
    }

    if (questionGroup.isDraft) {
      const draftedAtDate = stringToDate(questionGroup.attributes.drafted_at, 'backend_datetime')
      inlineInfo.push({
        icon: 'clock',
        message: `${i18n.t('surveys.answer.last_updated')} ${dateToString(
          draftedAtDate,
          'ui_short_with_time'
        )}`,
      })
    }

    if (hasFeature('reviews_weighted_average')) {
      inlineInfo.push({
        icon: 'calculator',
        message: i18n.t('surveys.answer.performance_score', { score: '-' }),
      })
    }

    return inlineInfo
  }

  if (useResponsivePage) {
    return (
      <ResponsivePage.Body
        showHeader={false}
        loading={loadingReview || loadingQuestionGroup || loadingCompetencies}
        error={!loadingReview && !loadingQuestionGroup && !review}
      >
        {questionGroup && isUserTheRespondent && (
          <EditableUntilBanner
            questionGroup={questionGroup}
            reviewIsNotActive={!review?.isActive}
          />
        )}
        <ScoreProvider>
          <Box flexDirection="column" width="full">
            {questionGroup && review && (
              <Answer
                review={review}
                questionGroup={questionGroup}
                inlineInfo={inlineInfo()}
                onDraftSaved={refresh}
                canReview={isUserTheRespondent}
                respondent={respondent}
              />
            )}
          </Box>
        </ScoreProvider>
      </ResponsivePage.Body>
    )
  }

  return (
    <Page
      isLoading={loadingReview || loadingQuestionGroup || loadingCompetencies}
      notFound={{
        active: !loadingReview && !loadingQuestionGroup && !review,
        title: i18n.t('errors.page_not_found.title'),
        description: i18n.t('errors.page_not_found.description'),
      }}
    >
      <Page.Header>
        <Header.Title backTo={back}>{reviewName ? reviewName : ''}</Header.Title>
        {questionGroup && isUserTheRespondent && (
          <EditableUntilBanner
            questionGroup={questionGroup as QuestionGroup}
            reviewIsNotActive={!review?.isActive}
          />
        )}
      </Page.Header>
      <Page.Body size="narrow">
        <ScoreProvider>
          <Box flexDirection="column" width="full">
            {questionGroup && (
              <Answer
                review={review as Review}
                questionGroup={questionGroup}
                inlineInfo={inlineInfo()}
                onDraftSaved={refresh}
                canReview={isUserTheRespondent}
                respondent={respondent}
              />
            )}
          </Box>
        </ScoreProvider>
      </Page.Body>
    </Page>
  )
}
